// @flow
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { append, includes, isEmpty, without } from "ramda";

import { Translate } from "@quantum/i18n";
import { NumberField } from "@quantum/ui";

import { anyIncludes } from "../../../../util";
import {
  type EdicaoMedida,
  type ErrosSelecao,
  type MedidaSelecionada,
  printaExpressaoRanking,
  COLUNAS_RANKING_COM_CASAS_DECIMAIS,
} from "../../../../model";
import {
  type State,
  selecionaColunasRanking,
  selecionaPesoRanking,
  getColunasRanking,
  getCasasDecimaisSelecionado,
  getDescricaoFuncaoRankingPorId,
  getEdicaoMedida,
  getFuncoesRanking,
  getMedidasRankeaveis,
  getNomeFuncaoRankingPorId,
  getPesosRanking,
  getSeparadorDecimal,
  i18n,
} from "../../../../state";

import "./Ranking.css";
import { SelecaoCasasDecimais } from "../../configuracoes";

const CheckboxGroupContext = React.createContext({
  value: new Set(),
  onChange: set => null,
});
const CheckboxGroup = ({ value, onChange, ...props }) => (
  <CheckboxGroupContext.Provider
    value={{ value: React.useMemo(() => new Set(value), [value]), onChange }}
    {...props}
  />
);

const CheckboxGroupOption = ({ value, children }) => {
  const { value: selectedValues, onChange } = React.useContext(
    CheckboxGroupContext
  );
  return children({
    checked: selectedValues.has(value),
    onChange: useCallback(
      event =>
        onChange(
          new Set(
            event.target.checked
              ? append(value, [...selectedValues])
              : without([value], [...selectedValues])
          )
        ),
      [onChange, value, selectedValues]
    ),
  });
};

export function processarRanking(
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
): Array<MedidaSelecionada> {
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);
  const colunas = [...(edicaoMedida.colunasRanking || [])];
  const pesos = edicaoMedida.pesosRanking || {};

  const casasDecimais = getCasasDecimaisSelecionado(state);

  return medidasSelecionadas.flatMap(medida =>
    colunas.map(coluna => ({
      ...medida,
      descricaoSelecao: getDescricaoFuncaoRankingPorId(coluna, state),
      expressao: printaExpressaoRanking({
        funcao: getNomeFuncaoRankingPorId(coluna, state),
        pesos,
      }),
      ...(includes(coluna, COLUNAS_RANKING_COM_CASAS_DECIMAIS)
        ? { casasDecimais }
        : {}),
    }))
  );
}

export const criaFuncaoValidacaoRanking = (
  state: State
) => (): ?ErrosSelecao => {
  const erros: ErrosSelecao = {};
  const { translate } = i18n(state);

  if (isEmpty(getColunasRanking(state)))
    erros.rankingSemColunas = translate(
      "configuracaoRelatorio.ranking.erro.semColunas"
    );
  if (isEmpty(getPesosRanking(state)))
    erros.rankingSemPesos = translate(
      "configuracaoRelatorio.ranking.erro.semPesos"
    );

  return isEmpty(erros) ? null : erros;
};

const Ranking = () => {
  const dispatch = useDispatch();
  const colunas = useSelector(getFuncoesRanking);
  const separadorDecimal = useSelector(getSeparadorDecimal);

  const colunasSelecionadas = useSelector(getColunasRanking);
  const aoMudarColunas = useCallback(
    colunas => dispatch(selecionaColunasRanking([...colunas])),
    [dispatch]
  );

  const exibirCasaDecimais = anyIncludes(
    COLUNAS_RANKING_COM_CASAS_DECIMAIS,
    colunasSelecionadas
  );

  const medidas = useSelector(getMedidasRankeaveis);
  const pesos = useSelector(getPesosRanking);

  return (
    <div className="Ranking">
      <h1 className="Ranking__titulo">Ranking</h1>
      <div className="Ranking__area-edicao">
        <div className="Ranking__colunas">
          <table className="Ranking__tabela">
            <thead>
              <tr>
                <td />
                <td>
                  <Translate>configuracaoRelatorio.ranking.colunas</Translate>
                </td>
              </tr>
            </thead>

            <tbody>
              <CheckboxGroup
                onChange={aoMudarColunas}
                value={colunasSelecionadas}
              >
                {colunas.map(coluna => (
                  <tr key={coluna.id}>
                    <td className="Ranking__coluna-checkbox">
                      <CheckboxGroupOption value={coluna.id}>
                        {checkbox => (
                          <input
                            id={`coluna-${coluna.id}`}
                            type="checkbox"
                            {...checkbox}
                          />
                        )}
                      </CheckboxGroupOption>
                    </td>
                    <td>
                      <label
                        className="Ranking__label-coluna"
                        htmlFor={`coluna-${coluna.id}`}
                      >
                        {coluna.traducao}
                      </label>
                    </td>
                  </tr>
                ))}
              </CheckboxGroup>
            </tbody>
          </table>
          {exibirCasaDecimais && (
            <SelecaoCasasDecimais className="Ranking__casas-decimais" />
          )}
        </div>

        <div className="Ranking__medidas">
          <table className="Ranking__tabela">
            <thead>
              <tr>
                <td className="Ranking__coluna-medida">
                  <Translate>configuracaoRelatorio.ranking.medidas</Translate>
                </td>
                <td className="Ranking__coluna-peso">
                  <Translate>configuracaoRelatorio.ranking.pesos</Translate>
                </td>
              </tr>
            </thead>

            <tbody>
              {medidas.length === 0 && (
                <tr>
                  <td colSpan={2}>
                    <Translate>
                      configuracaoRelatorio.compostas.erro.semMedidasEligiveis
                    </Translate>
                  </td>
                </tr>
              )}

              {medidas.map((medida, i) => (
                <tr key={i}>
                  <td className="Ranking__coluna-medida">
                    {medida.label || medida.descricaoSelecao}
                  </td>
                  <td className="Ranking__coluna-peso">
                    <NumberField
                      allowNegative
                      decimalSeparator={separadorDecimal}
                      thousandSeparator=""
                      decimalScale={2}
                      className="Ranking__peso-input"
                      type="text"
                      value={pesos[medida.alias] || ""}
                      onChangeValue={peso =>
                        dispatch(selecionaPesoRanking(medida.alias, peso))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Ranking;
